import z from 'zod'

/** WGS-84 point used as geofilter center or player/venue fallback. */
export const geoLatLngSchema = z.object({
  latitude: z.number().finite().min(-90).max(90),
  longitude: z.number().finite().min(-180).max(180)
})

export type TGeoLatLng = z.infer<typeof geoLatLngSchema>

/** One include-circle for a playlist assignment geofilter. */
export const playlistAssignmentGeoCircleSchema = z.object({
  center: geoLatLngSchema,
  /** Inclusive radius in meters (UI may display km). */
  radiusMeters: z.number().finite().positive()
})

export type TPlaylistAssignmentGeoCircle = z.infer<typeof playlistAssignmentGeoCircleSchema>

/**
 * Coerce legacy single-circle `{ center, radiusMeters }` into `{ circles: [...] }`.
 * Stored JSON from v1 rows keeps working without a data migration.
 */
function coerceLegacyPlaylistAssignmentGeoFilter(data: unknown): unknown {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return data
  }
  const o = data as Record<string, unknown>
  if (Array.isArray(o.circles)) {
    return o
  }
  if (o.center != null && o.radiusMeters != null) {
    return {
      circles: [{ center: o.center, radiusMeters: o.radiusMeters }]
    }
  }
  return o
}

/**
 * Optional geographic gate for a distribution-list playlist assignment.
 * Null / omitted on the row = no geo gate (always geographically eligible).
 * Matching is OR across {@link circles} (inside any circle → eligible).
 */
export const playlistAssignmentGeoFilterSchema = z.preprocess(
  coerceLegacyPlaylistAssignmentGeoFilter,
  z.object({
    circles: z.array(playlistAssignmentGeoCircleSchema).min(1)
  }).strip()
)

export type TPlaylistAssignmentGeoFilter = z.infer<typeof playlistAssignmentGeoFilterSchema>

export function parsePlaylistAssignmentGeoFilter(v: unknown): TPlaylistAssignmentGeoFilter | null {
  const r = playlistAssignmentGeoFilterSchema.safeParse(v)
  return r.success ? r.data : null
}

/** Circles on a parsed filter (always ≥1 when filter is non-null). Accepts legacy single-circle rows. */
export function geoFilterCircles(
  filter: TPlaylistAssignmentGeoFilter | null | undefined | {
    center?: TGeoLatLng
    radiusMeters?: number
    circles?: TPlaylistAssignmentGeoCircle[]
  }
): TPlaylistAssignmentGeoCircle[] {
  if (filter == null) {
    return []
  }
  if (Array.isArray(filter.circles) && filter.circles.length > 0) {
    return filter.circles
  }
  const legacy = filter as { center?: TGeoLatLng, radiusMeters?: number }
  if (
    legacy.center != null
    && typeof legacy.radiusMeters === 'number'
    && Number.isFinite(legacy.radiusMeters)
    && legacy.radiusMeters > 0
  ) {
    return [{ center: legacy.center, radiusMeters: legacy.radiusMeters }]
  }
  return []
}

/** Largest radius among circles; used to decide when GPS accuracy is too coarse. */
export function maxGeoFilterRadiusMeters(
  filter: TPlaylistAssignmentGeoFilter | null | undefined
): number | null {
  const circles = geoFilterCircles(filter)
  if (circles.length === 0) {
    return null
  }
  let max = 0
  for (const c of circles) {
    if (c.radiusMeters > max) {
      max = c.radiusMeters
    }
  }
  return max > 0 ? max : null
}

export function createDefaultPlaylistAssignmentGeoFilter(
  center: TGeoLatLng = { latitude: -23.5505, longitude: -46.6333 },
  radiusMeters = 1000
): TPlaylistAssignmentGeoFilter {
  return {
    circles: [{ center: { ...center }, radiusMeters }]
  }
}
